// Names AND relevance — the single contract every operator surface imports.
//
// WHY BOTH LIVE HERE: a screen that renames a rail but sorts it by a local
// predicate drifts within a week. Names and "is this row worth showing" are
// the same decision made twice, so they ship as one module. `format.ts` keeps
// numbers / dates / assets and does NOT grow a naming section.
//
// FOUNDATIONAL: the UI never trusts the stored string. Every name is derived
// at render time from whatever the row happens to carry (operator display
// name, treasury slug, Formance account path). Nothing here mutates data;
// renames go through the BFF via the reviewable script, never from a screen.
//
// ── RAIL GRAMMAR ────────────────────────────────────────────────────────────
//   `{Brand}[ · {Qualifier}]` — one slot, one separator, ` · ` (U+00B7).
//   `—`, `-`, `/`, `|` and bare-space joins are banned in output. Asset codes
//   keep the TIGHT form `USDT·TRC`; spaced separates a name, tight separates
//   an asset, and the two are never nested (the asset lives in `trailing`).
//   Brand first, always — `Bhargav — SBI` → `SBI · Bhargav`.
//   Qualifier is AT MOST ONE: (1) holder, (2) non-home region, (3) nothing.
//   Home region (UAE) is never marked; `HTX` and `HTX · India` must not
//   collide, so non-home IS marked. Legacy entity prefixes are dropped —
//   `IndiaOTC — RBL` → `RBL · India` — it is one roof, one book.
//   Primary is designed to fit 28 chars; `Name` truncates with the full value
//   in `title` and never wraps. Longest real name today is 21 chars
//   (`Angadiya cash · India`, which is already lawful and stays byte-identical
//   — proof the grammar was derived from the data, not imposed on it).
//
// ── PARTY NORMALIZATION ─────────────────────────────────────────────────────
//   trim/collapse → strip trailing `. , - ;` (unless the final token is a
//   brand literal like `Liv.`) → strip source suffixes (TG/Telegram/AC/A-C/
//   OTC/B2B/Ref) ONLY as space-delimited trailing tokens, never inside a
//   hyphenated compound (`VM-OTC` survives) and never if it empties the name
//   → Title Case each token except ACRONYMS and brand-map members.
//   Human names are never re-spelled: casing and punctuation only.
//   A profane name is displayed AS-IS with a review flag — never auto-renamed,
//   never masked. The string may encode operator knowledge; masking hides an
//   identity from the one person who needs it.
//
// ── RELEVANCE ───────────────────────────────────────────────────────────────
//   Dust is per-asset, not universal — a flat `|balance| >= 1` hides half a
//   bitcoin. Four tiers, one comparator, and "stale" means exactly tier 3.
//   Per surface: pickers hide behind `Show all (N)`, Treasury collapses but
//   never hides (subtotals always include collapsed rows), trades never
//   filter — a trade is evidence.

import {
  ALL_CAPS,
  displayAsset,
  fmtAmount,
  fmtRelative,
  prettyTreasurySlug,
  shortAccount,
  smartCase,
} from './format';

/* ══════════════════ structural input types ══════════════════
   Deliberately loose and all-optional: BFF payloads differ per endpoint, so
   `TreasuryRow`, `GroupLine`, `GroupSource`, `PartySummary` and
   `PartyDetail['party']` all satisfy these with no adapters and no casts. */

/** Per-asset balances in either shape the BFF ships them. */
export type BalanceBag =
  | Record<string, number>
  | Array<{ asset?: string | null; balance?: number | null }>;

export interface RelevanceLike {
  asset?: string | null;
  balance?: number | null;
  /** A rail the registry vouches for that has never been posted to. */
  known?: boolean | null;
  balances?: BalanceBag | null;
  is_favorite?: boolean | null;
  /** ISO timestamp of the last deal / posting, when the endpoint knows it. */
  last_activity_at?: string | null;
  lastActivityAt?: string | null;
}

export interface RailLike extends RelevanceLike {
  /** admin.parties.display_name — operator-editable, authoritative. */
  label?: string | null;
  slug?: string | null;
  account?: string | null;
  provider?: string | null;
  persona?: string | null;
  treasuryKind?: string | null;
}

export interface PartyLike extends RelevanceLike {
  slug?: string | null;
  display_name?: string | null;
  party_type?: string | null;
  counterparty_role?: string | null;
  profile_kind?: string | null;
  trust_tier?: string | null;
  treasury_slug?: string | null;
}

/** Anything nameable — lets one comparator sort rails and parties alike. */
export type NameLike = RailLike & PartyLike;

/** 0 PINNED · 1 LIVE · 2 QUIET · 3 STALE. Named so screens can type their
 *  `maxTier` prop without restating the union (§4.3). */
export type Tier = 0 | 1 | 2 | 3;

/* ══════════════════ brand table (the only one in the codebase) ══════════════ */

/** Canonical casing for every brand the book knows. Keys are lowercase and
 *  punctuation-free; values are what the operator reads. Slug-derivation
 *  mangled these (`Htx`, `Okx`, `Kucoin`) — this table is the fix. */
export const BRAND_MAP: Readonly<Record<string, string>> = {
  // UAE banks
  wio: 'Wio', enbd: 'ENBD', mashreq: 'Mashreq', rak: 'RAK', rakbank: 'RAK',
  adcb: 'ADCB', fab: 'FAB', cbd: 'CBD', dib: 'DIB', alux: 'ALUX',
  // Liv. is Emirates NBD's digital bank — the trailing dot is part of the
  // brand, which is why punctuation-stripping checks this table first.
  //
  // `live` is deliberately NOT a key. The book has a real rail stored as
  // "Live — Bhavy", and mapping `live → Liv.` would re-spell live operator
  // data on a hunch — the one thing D1 forbids. Unmapped renders imperfectly
  // (`Live · Bhavy`), never wrongly. If it really is the bank, one operator
  // rename fixes it for good. See open_issues.
  liv: 'Liv.', 'liv.': 'Liv.',
  // India banks
  rbl: 'RBL', pnb: 'PNB', au: 'AU', sbi: 'SBI', sib: 'SIB',
  hdfc: 'HDFC', icici: 'ICICI', axis: 'Axis', kotak: 'Kotak', idfc: 'IDFC',
  // India banks found on the live book by scripts/normalize-names.mjs's first
  // dry run. They existed only in that script's twin table, which meant the
  // renamer would WRITE `UCO · India` while this module rendered the stored
  // string back as something else. Keep the two tables in lockstep.
  //
  // Only codes that are NOT also human names live here — this table is shared
  // with the PARTY path (via brandCase/segmentWords), so anything ambiguous
  // goes in RAIL_BRAND_MAP instead. See the note there.
  federal: 'Federal', fedral: 'Federal',      // 'fedral' is a real misspelling in the book
  uco: 'UCO', psb: 'PSB', iob: 'IOB', kvb: 'KVB', tmb: 'TMB',
  cboi: 'CBOI', boi: 'BOI', idbi: 'IDBI', csb: 'CSB',
  cub: 'CUB', dcb: 'DCB', canara: 'Canara', cosmo: 'Cosmo',
  bandhan: 'Bandhan', ujjivan: 'Ujjivan', indusind: 'IndusInd',
  // Exchanges
  binance: 'Binance', bybit: 'Bybit', htx: 'HTX', huobi: 'Huobi',
  okx: 'OKX', okex: 'OKX', kucoin: 'KuCoin', bitget: 'Bitget',
  paxful: 'Paxful', gate: 'Gate', gateio: 'Gate', coinex: 'CoinEx',
  mexc: 'MEXC', kraken: 'Kraken', coinbase: 'Coinbase',
  // Wallets / cash rails
  ledger: 'Ledger', trezor: 'Trezor', tronlink: 'TronLink',
  metamask: 'MetaMask', angadiya: 'Angadiya',
  safepal: 'SafePal', exodus: 'Exodus', trustwallet: 'Trust Wallet',
  // Entities (kept so brandCase() fixes them anywhere they survive; railName
  // drops them outright as legacy prefixes).
  dubaiotc: 'DubaiOTC', indiaotc: 'IndiaOTC', euotc: 'EuOTC',
};

/** Acronyms that stay upper-case through Title Case. NOT a brand table — it
 *  is the "don't sentence-case this" list (`HK` stays, `KIM` becomes `Kim`
 *  precisely because KIM is not in here).
 *
 *  Seeded from `format.ts`'s ALL_CAPS rather than redeclared, so the city
 *  codes, asset codes and domain shorthand that `smartCase()` already respects
 *  cannot drift away from what this module respects. Only the entity/company
 *  suffixes that are meaningless to slug-casing are added on top. One table,
 *  extended — not a second table. */
const ACRONYMS: ReadonlySet<string> = new Set([
  ...ALL_CAPS,
  'us', 'eu',
  'llc', 'fz', 'fze', 'fzc', 'dmcc',
]);

/** Legacy entity prefixes. Value = the region they imply once dropped;
 *  the home entity implies nothing, because home region is never marked. */
const ENTITY_REGION: Readonly<Record<string, string>> = {
  dubaiotc: '', indiaotc: 'India', euotc: 'EU',
};

/** Home = UAE. Never marked — pickers group by kind, not geography. */
const HOME_REGION: ReadonlySet<string> = new Set([
  'uae', 'ae', 'dubai', 'dxb', 'auh', 'shj', 'emirates',
]);

/** Non-home regions, which ARE marked so `HTX` and `HTX · India` differ. */
const REGION_MAP: Readonly<Record<string, string>> = {
  // `in` is NOT a key: it is an English preposition long before it is a
  // country code, and "Cash in transit" must not become "Cash · India".
  india: 'India', bharat: 'India',
  hk: 'HK', hongkong: 'HK', sg: 'SG', singapore: 'SG',
  uk: 'UK', eu: 'EU', us: 'US',
};

/** Chain codes for a CRYPTO rail are the wallet's identity, not decoration.
 *
 *  This used to drop them outright, reasoning that "the asset chip already says
 *  TRC". That premise is false: a wallet holds SEVERAL assets — `crypto.hot-erc`
 *  carries USDC-ERC and USDT-ERC — so the chip describes the ASSET and never the
 *  WALLET. With the code stripped, `crypto.hot-trc`, `crypto.hot-erc` and
 *  `crypto.hot-sol` all rendered "Hot · wallet": three different addresses, one
 *  name, on the Move money picker where choosing the wrong one sends coins off
 *  the wrong chain. Found by the operator on go-live day, 2026-08-26.
 *
 *  So the code is now CAPTURED and used as the qualifier. Tight `USDT·TRC` still
 *  never nests inside spaced ` · ` — the qualifier is the bare chain, `TRC20`. */
const CHAIN_TOKENS: ReadonlySet<string> = new Set([
  'trc', 'erc', 'bep', 'trc20', 'erc20', 'bep20', 'sol', 'spl',
  // Native-coin chains name their wallet the same way. Without these,
  // `crypto.hot-btc` ("Hot wallet · BTC") fell through to the leftover bucket
  // and came out "Hot · wallet BTC" — the qualifier stranded after the noun.
  'btc', 'eth', 'bnb',
  // Base (19 Sept 2026): `crypto.hot-base` is the desk's EVM key on the Base
  // network — a chain, so the wallet is named for it like its siblings.
  'base',
]);

/** Cash boxes keep the noun: "Office" alone collides with the Office entity. */
const CASH_NOUN: ReadonlySet<string> = new Set(['cash']);

/** Structural nouns that describe WHAT a rail is, not WHO it belongs to.
 *
 *  Rail-only (consulted in `parseRail`, never on the party path). Without this,
 *  the trailing noun of a two-word bank name landed in the HOLDER slot, and
 *  because a rail gets exactly one qualifier the real region was then dropped:
 *  `Yes Bank · India` → `Yes · Bank`, `Federal Bank · India` → `Federal · Bank`.
 *  The brand table already carries the full brand (`yes` → `Yes Bank`), so the
 *  bare noun is redundant once a brand is present and is simply dropped.
 *
 *  It is NOT dropped when it is the only word left — a rail stored as just
 *  "Bank" still has to render something, so the fallback path keeps it. */
const BANK_NOUN: ReadonlySet<string> = new Set(['bank']);

/** Leading slug segments that name a CLASS, not a rail — dropped when the slug
 *  has more to say. `cash` is deliberately absent: it is the one kind whose
 *  noun survives into the name (`Office cash`). */
const KIND_SEGMENTS: ReadonlySet<string> = new Set([
  'bank', 'exchange', 'hot', 'cold', 'crypto', 'wallet', 'treasury', 'assets',
]);

/** Possessive determiners are KEPT when a real noun follows: `My Ledger` reads
 *  `My Ledger`, which is what it is called in legacy, in the cutover seed and by
 *  the operator.
 *
 *  It used to render `Ledger · Desk`, to stop `My Ledger` and `Loki's Ledger`
 *  collapsing onto one "Ledger". That collision is gone — `Loki's Ledger` was
 *  retired to `legacy.loki-s-ledger` and archived — so the stand-in outlived its
 *  reason and did real harm: it invented a word ("Desk") that appears on no
 *  other screen, so the same rail read `My Ledger` in Treasury and legacy but
 *  `Ledger · Desk` in Move money. Operator, go-live day: "mismatch for the same
 *  party at diff screens".
 *
 *  The ghost still stands in when the possessive is the ONLY identifying word,
 *  because a bare "My" names nothing. */
const POSSESSIVES: ReadonlySet<string> = new Set(['my', 'our', 'mine', 'ours']);
const AMBIGUOUS_HOLDER = 'Desk';

/** Pure filler — these really do vanish. */
const DROP_WORDS: ReadonlySet<string> = new Set(['the', 'a', 'of', 'and']);

/** Trailing source suffixes on party names — where the contact came from is
 *  not their name. Stripped only as whole trailing tokens.
 *
 *  The test each member passes: the information it carries SURVIVES ELSEWHERE
 *  in the payload, so removing it from the name relocates rather than deletes.
 *    TG / Telegram → `telegram_username` (PartyDetail renders the handle)
 *    AC / A/C      → `party_type = 'own_account'`
 *    OTC / B2B     → `counterparty_role = 'merchant'`
 *  …and all three are re-stated on the secondary line as metadata.
 *
 *  `ref` fails that test and is therefore absent, even though §2.1.3 lists it:
 *  nothing in the payload records "came via a referral", so stripping it would
 *  delete operator knowledge outright. §2.4's own worked row on the real
 *  string agrees — `Oussama Ref.` renders `Oussama Ref`. The doc contradicts
 *  itself here and this is the non-destructive reading; flagged for the doc
 *  owner in open_issues. */
const SOURCE_SUFFIXES: ReadonlySet<string> = new Set([
  'tg', 'telegram', 'ac', 'a/c', 'a-c', 'otc', 'b2b',
]);

// Deliberately narrow: this flags a name for human review, it never rewrites
// or masks one. Kept as word-ish fragments so "Scunthorpe" problems stay out
// of the operator's way (a false flag costs one glance; a false rename costs
// operator knowledge).
const PROFANITY = /\b(fuck|shit|cunt|bitch|bastard|wank)/i;

/** Placeholder names that carry no identity — worth a review nudge. */
const PLACEHOLDER: ReadonlySet<string> = new Set([
  'unknown', 'test', 'testing', 'new', 'customer', 'party', 'tbd', 'na', 'n/a', 'xxx', '???',
]);

/* ══════════════════ small string helpers ══════════════════ */

function collapse(s: string): string {
  return s.replace(/\s+/g, ' ').trim();
}

function stripPossessive(w: string): string {
  return w.replace(/['’]s$/i, '').replace(/['’]$/, '');
}

function brandKey(w: string): string {
  return w.toLowerCase().replace(/^[^\p{L}\p{N}.]+|[^\p{L}\p{N}.]+$/gu, '');
}

/**
 * Bank brands whose key is ALSO a perfectly ordinary human name or English
 * word. Consulted ONLY on the rail path (`parseRail`) — never by `brandCase()`,
 * `segmentWords()` or anything a party name flows through.
 *
 * This split is not cosmetic. These four keys were briefly in BRAND_MAP and the
 * shared path immediately re-spelled real customers: `Bob` → `BOB`,
 * `Bob Sharma` → `BOB Sharma`, `Yes` → `Yes Bank`. A rail has a treasury slug
 * and a kind to prove it is a bank; a party has neither, so it must not be
 * guessed at. scripts/normalize-names.mjs makes the same split for the same
 * reason (its BRAND_LITERALS vs BRAND_MAP) — keep the two in step.
 */
const RAIL_BRAND_MAP: Readonly<Record<string, string>> = {
  bob: 'BOB',                 // Bank of Baroda — also a first name
  yes: 'Yes Bank',            // also the English word
  yesbank: 'Yes Bank',
  indian: 'Indian Bank',      // also an adjective
};

/** Canonical brand casing for one token, or '' when it is not a brand. */
function brandOf(word: string): string {
  const k = brandKey(word);
  return BRAND_MAP[k] ?? BRAND_MAP[k.replace(/\.+$/, '')] ?? '';
}

/** Fix brand casing anywhere in an arbitrary string, leaving everything else
 *  alone. Replaces the per-screen BRAND_CASE tables that used to drift. */
export function brandCase(s: string): string {
  if (!s) return '';
  // Word runs, not whitespace-split tokens: a brand glued to punctuation
  // (`IndiaOTC—RBL`, `(Wio)`) still gets fixed, and — more importantly — the
  // punctuation around it survives. Splitting on whitespace and handing the
  // whole token to brandOf() silently ATE those characters, because brandOf
  // strips them to build its lookup key and then returns the bare brand.
  return s.replace(/[\p{L}\p{N}]+\.?/gu, (tok) => brandOf(tok) || tok);
}

/** Slug segment → readable word. `p-jyothi` → `P Jyothi`, `dxb` → `DXB`. */
function segmentWords(seg: string): string[] {
  return seg
    .split(/[-_]+/)
    .filter(Boolean)
    .map((w) => {
      const low = w.toLowerCase();
      const b = BRAND_MAP[low];
      if (b) return b;
      if (ACRONYMS.has(low)) return low.toUpperCase();
      return low.charAt(0).toUpperCase() + low.slice(1);
    });
}

/** Split a stored display name into words, treating every banned separator
 *  (`—` `–` `·` `|` `/` `,` and the spaced hyphen) as a word boundary.
 *  Intra-word hyphens survive: `VM-OTC` and `Grow-more` stay one token. */
function labelWords(s: string): string[] {
  return collapse(s)
    .replace(/\s+[-‐-―]\s+/g, ' ')
    .replace(/[—–·|/,]+/g, ' ')
    .split(/\s+/)
    .filter(Boolean);
}

/** Formance account path → rail segments. `treasury:dubaiotc:cash:office:fiat:aed`
 *  → ['cash','office']. Trailing `{fiat|crypto}:{asset}` (or a bare asset) and
 *  the `treasury:{entity}` head are plumbing, not name. */
function accountSegments(account: string): string[] {
  const parts = account.split(':').filter(Boolean);
  if (parts[0] !== 'treasury' || parts.length < 4) return [];
  const rest = parts.slice(2);
  const catIdx = rest.length - 2;
  const hasCategory = catIdx >= 0 && (rest[catIdx] === 'fiat' || rest[catIdx] === 'crypto');
  const railSegs = hasCategory ? rest.slice(0, catIdx) : rest.slice(0, -1);
  return railSegs[0] === 'legacy' ? railSegs.slice(1) : railSegs;
}

/** Raw rail words from whatever the row carries, best source first. */
/** Did the source already say "these are two different things"? A banned
 *  separator in a stored label (`Live — Bhavy`) and a multi-segment slug
 *  (`bank.enbd.nk`) both mean yes. It matters only when no brand was found:
 *  with a brand the grammar knows which slot is which, but without one this is
 *  the only evidence distinguishing `Live — Bhavy` (two slots → `Live · Bhavy`)
 *  from `Acquired accounts` (one noun phrase, and joining it with ` · ` would
 *  invent a distinction the data never made). */
const SPLIT_HINT = /[—–·|/,]|\s[-‐-―]\s/;

interface RailSource {
  words: string[];
  split: boolean;
}

function railWords(row: RailLike): RailSource {
  const label = (row.label ?? '').trim();
  if (label) return { words: labelWords(label), split: SPLIT_HINT.test(label) };

  const slug = (row.slug ?? '').trim();
  if (slug) {
    const segs = dropKindSegment(slug.split('.').filter(Boolean));
    return { words: segs.flatMap(segmentWords), split: segs.length > 1 };
  }

  const pp = [row.provider, row.persona].filter((x): x is string => !!x);
  if (pp.length > 0) {
    const segs = dropKindSegment(pp);
    return { words: segs.flatMap(segmentWords), split: segs.length > 1 };
  }

  const account = (row.account ?? '').trim();
  if (account) {
    const segs = dropKindSegment(accountSegments(account));
    if (segs.length > 0) return { words: segs.flatMap(segmentWords), split: segs.length > 1 };
  }
  return { words: [], split: false };
}

/** Strip the `legacy.` marker and a leading class segment, but never strip a
 *  slug down to nothing. */
function dropKindSegment(segs: string[]): string[] {
  const body = segs[0] === 'legacy' ? segs.slice(1) : segs;
  const head = (body[0] ?? '').toLowerCase();
  return body.length > 1 && KIND_SEGMENTS.has(head) ? body.slice(1) : body;
}

/* ══════════════════ rail names ══════════════════ */

interface RailParts {
  brand: string;
  holder: string;
  region: string;
  /** A home-region token, kept only to fill a cash box's place slot. */
  homePlace: string;
  rest: string[];
  hasCash: boolean;
  /** A possessive determiner was present ("My Ledger"). */
  possessive: boolean;
  /** The determiner itself, so a name that reads fine with it keeps it. */
  possessiveWord: string;
  /** Chain code of a crypto rail, upper-cased — the wallet's identity, and the
   *  qualifier that outranks every other. Empty for non-crypto rails. */
  chain: string;
}

function parseRail(words: string[]): RailParts {
  let brand = '';
  let holder = '';
  let region = '';
  let homePlace = '';
  let hasCash = false;
  let possessive = false;
  let possessiveWord = '';   // kept — see POSSESSIVES
  let chain = '';            // kept — see CHAIN_TOKENS
  let sawBankNoun = false;
  const rest: string[] = [];

  for (const raw of words) {
    const word = stripPossessive(raw);
    const key = brandKey(word);
    if (!key) continue;

    // Legacy entity prefix: dropped outright; it may leave a region behind.
    const entityRegion = ENTITY_REGION[key];
    if (entityRegion !== undefined) {
      if (entityRegion && !region) region = entityRegion;
      continue;
    }
    if (CASH_NOUN.has(key)) { hasCash = true; continue; }
    if (BANK_NOUN.has(key)) { sawBankNoun = true; continue; }
    if (POSSESSIVES.has(key)) { possessive = true; if (!possessiveWord) possessiveWord = word; continue; }
    if (DROP_WORDS.has(key)) continue;
    if (CHAIN_TOKENS.has(key)) { if (!chain) chain = word.toUpperCase(); continue; }
    if (HOME_REGION.has(key)) {               // home is never marked as a qualifier…
      if (!homePlace) homePlace = word;       // …but `cash.dxb` is still "DXB cash"
      continue;
    }
    const reg = REGION_MAP[key];
    if (reg) { if (!region) region = reg; continue; }

    // Rail-only brands win here: this is the one context where a bare `bob`
    // provably means Bank of Baroda rather than a person.
    const bare = key.replace(/\.+$/, '');
    const b = RAIL_BRAND_MAP[key] ?? RAIL_BRAND_MAP[bare] ?? BRAND_MAP[key] ?? BRAND_MAP[bare];
    if (b) {
      if (!brand) brand = b;
      else if (!holder) holder = b;
      else rest.push(b);
      continue;
    }
    if (!holder) holder = word;
    else rest.push(word);
  }
  // "Bank" is dropped as a structural noun only when something else survived to
  // name the rail. If it was the ONLY word, it is all we have — restore it
  // rather than fall through to a slug.
  if (sawBankNoun && !brand && !holder && !region && !homePlace && !hasCash && rest.length === 0) {
    holder = 'Bank';
  }
  return { brand, holder, region, homePlace, rest, hasCash, possessive, chain, possessiveWord };
}

/**
 * Lawful display name for a rail. Pure, synchronous, total — never throws,
 * never returns undefined, and accepts partially-populated rows.
 */
/** Last resort when the grammar yields nothing. Never a raw slug and never a
 *  raw Formance account path — conformance rule 1 says neither may reach the
 *  DOM, so even the give-up path is cleaned. */
function railFallback(row: RailLike): string {
  const label = (row.label ?? '').trim();
  // A label of pure punctuation ("·", "///") is not a name — fall through to
  // the structural sources rather than printing separator debris.
  if (label && /[\p{L}\p{N}]/u.test(label)) return brandCase(label);
  if (row.slug) {
    const pretty = prettyTreasurySlug(row.slug);
    if (pretty && pretty !== '—') return brandCase(pretty);
  }
  const account = (row.account ?? '').trim();
  if (account) return brandCase(shortAccount(account));
  return '—';
}

/** THE ASSET FOLD — project-wide, hardcoded (operator, 1 Sept 2026: "hard code
 *  those rules projectwise. name should be in sync and 100% tidy").
 *  books-v3 speaks bare USDT/USDC — the chain lives in the WALLET name, never
 *  the asset code — while the v2 dialect and the chain feeds still say
 *  USDTTRC / USDCERC. Every comparison of asset codes goes through this fold;
 *  a raw `===` on asset strings is a bug (it rendered a funded Hot wallet as
 *  0.00 in the rail picker on day one). */
export function foldAsset(code: string | null | undefined): string {
  const c = (code ?? '').toUpperCase();
  if (c.startsWith('USDT')) return 'USDT';
  if (c.startsWith('USDC')) return 'USDC';
  return c;
}
/** A PARTY's box key for an asset. The server nets every stablecoin a party holds onto ONE `USDT` dollar line
 *  (bff/src/lib/assets.ts netPartyStablecoins, the ruling of 25 Aug 2026), so a USDC trade reads that line — there is no
 *  `USDC` key to find ("I am noit seeing his usdc balance", 4 Nov 2026; "yes even if they fold, I should see the remaining
 *  balance"). Treasury rails keep their own codes: never use this on a rail. */
export function partyBoxKey(code: string | null | undefined): string {
  const f = foldAsset(code);
  return f === 'USDC' ? 'USDT' : f;
}
/** A PARTY's balance in an asset, read from its box by partyBoxKey: a stablecoin reads the one netted dollar line; every
 *  other asset its fold. The one reader for a party's box by asset (the CC, 4 Nov 2026: Book trade's held check, Record
 *  money, Adjust, Profit share, Sub-broker and the cheque all looked a USDC amount up under a `USDC` key that is never sent,
 *  and read 0). Never on a treasury rail. */
export function partyBalanceOf(balances: Readonly<Record<string, number | string>> | null | undefined, code: string | null | undefined): number {
  const key = partyBoxKey(code);
  if (!key) return 0;
  let sum = 0;
  for (const [k, v] of Object.entries(balances ?? {})) if (partyBoxKey(k) === key) sum += Number(v) || 0;
  return sum;
}
/** What the desk holds of the party's, in an asset (sign law: a negative box is the desk holding theirs); 0 when nothing. */
export function deskHolds(balances: Readonly<Record<string, number | string>> | null | undefined, code: string | null | undefined): number {
  const v = partyBalanceOf(balances, code);
  return v < 0 ? -v : 0;
}
export function sameAsset(a: string | null | undefined, b: string | null | undefined): boolean {
  return foldAsset(a) === foldAsset(b);
}

export function railName(row: RailLike): string {
  // ── A STORED LABEL IS THE NAME. Render it. ────────────────────────────────
  //
  // Same rule as normalizeParty(), and it belongs here for the same reason.
  // The parser below exists to make sense of a raw slug or a provider string;
  // it was also being run over labels a human had curated, and it rearranged
  // them: `OTC · PNB` came out `PNB · OTC`, because the parser recognises PNB
  // as a bank brand and promotes a brand to the primary slot. So the operator
  // set the owner-first order the desk actually uses, the Parties screen showed
  // it, and every picker showed the reverse — the same rail under two names.
  //
  // The owner prefix is the identification (operator, 2026-08-26: "that is a
  // account name identification, as many banks exists under different account
  // owner"), so re-ordering it destroys the very thing the label is for.
  //
  // Derivation is untouched below and still runs for a rail with NO label.
  const stored = (row.label ?? '').trim().replace(/\s+/g, ' ');
  if (stored) return stored;

  const { words, split } = railWords(row);
  if (words.length === 0) return railFallback(row);

  const { brand, holder, region, homePlace, rest, hasCash, possessive, chain, possessiveWord } = parseRail(words);
  // `My Ledger` keeps its determiner; the ghost is only for a bare possessive.
  const namesHavePossessive = !!possessiveWord && (!!holder || !!brand || rest.length > 0);
  const ghost = possessive && !namesHavePossessive ? AMBIGUOUS_HOLDER : '';

  let primary: string;
  let qualifier: string;

  if (hasCash) {
    // Cash boxes keep the noun — `{Place} cash`. `homePlace` is why the slug
    // `cash.dxb` renders "DXB cash" and not a bare "Cash": a home-region token
    // is never a *qualifier*, but it is still the box's place.
    const place = brand || holder || homePlace || rest.join(' ') || ghost;
    primary = place ? `${place} cash` : 'Cash';
    qualifier = brand && holder ? holder : region;
  } else if (brand) {
    // Holder beats region, always — a rail with a named holder is already
    // unique and the region would only make it longer.
    primary = possessiveWord && !holder ? `${possessiveWord} ${brand}` : brand;
    qualifier = chain || holder || (possessiveWord ? '' : ghost) || region;
  } else {
    const names = [holder, ...rest].filter(Boolean);
    if (namesHavePossessive) names.unshift(possessiveWord);
    // A crypto rail's chain IS its identity — it outranks every other qualifier.
    if (chain && names.length) return `${names.join(' ')} · ${chain}`;
    if (split && names.length > 1) {
      // The source already carried two slots; keep them, just lawfully joined.
      primary = names[0]!;
      qualifier = names.slice(1).join(' ');
    } else {
      primary = names.join(' ');
      qualifier = primary ? region : '';
    }
    if (!primary) primary = region || ghost || '';
  }

  if (!primary) return railFallback(row);
  return qualifier ? `${primary} · ${qualifier}` : primary;
}

/** Rail kind vocabulary — THE table. It previously existed three times with
 *  three different words for the same thing: `RailPicker.KIND_LABEL` said
 *  `Cold storage`, `TreasuryBalances.KIND_META` said `Cold wallets`, and the
 *  row subtitle here said `Cold wallet`. One rail kind, three spellings across
 *  three screens is exactly the inconsistency the operator complained about, so
 *  both forms now live here and the screens import them.
 *
 *  `one` is the singular row form (`Bank · Quiet`); `many` is the plural
 *  section-header form (`Banks`). Unknown kinds fall through `smartCase()` so a
 *  raw enum can never reach the DOM (conformance rule 1). */
const RAIL_KIND: Readonly<Record<string, { one: string; many: string; order: number }>> = {
  // ── ORDER IS THE OPERATOR'S 95% CASE, NOT THE ALPHABET ────────────────────
  // Money reaches this desk in a crypto wallet or in office cash. Everything
  // else — 40 bank rails, 9 exchanges, 20 legacy husks — is the long tail.
  //
  // `crypto` was MISSING from this table until 2026-08-24, so it fell through
  // to the default order 50 and every wallet sorted BELOW the exchanges and
  // banks. Opening a picker to take a USDT deposit showed six exchanges and no
  // wallet at all. The rails were renamed to `crypto.*` that morning; this
  // table was not updated with them.
  crypto:   { one: 'Wallet',      many: 'Wallets',      order: 1 },
  hot:      { one: 'Hot wallet',  many: 'Hot wallets',  order: 2 },
  cold:     { one: 'Cold wallet', many: 'Cold wallets', order: 3 },
  cash:     { one: 'Cash',        many: 'Cash',         order: 4 },
  angadiya: { one: 'Angadiya',    many: 'Angadiya',     order: 5 },
  exchange: { one: 'Exchange',    many: 'Exchanges',    order: 6 },
  bank:     { one: 'Bank',        many: 'Banks',        order: 7 },
  legacy:   { one: 'Legacy',      many: 'Legacy',       order: 99 },
};

/** The kinds money actually arrives in. A picker leads with these and parks the
 *  rest behind one "More" footer — operator, 2026-08-24: "we received either in
 *  hot crypto wallets or cash aed 95%… erc/trc n all wallets". */
export const PRIMARY_RAIL_KINDS: ReadonlySet<string> =
  new Set(['crypto', 'hot', 'cold', 'cash', 'angadiya']);

function railKindLabel(row: RailLike): string {
  const k = (row.treasuryKind ?? row.slug?.split('.')[0] ?? '').toLowerCase();
  if (!k) return '';
  return RAIL_KIND[k]?.one ?? smartCase(k);
}

/** Singular row form for a bare kind slug (`bank` → `Bank`). */
export function railKindName(kind: string | null | undefined): string {
  const k = (kind ?? '').toLowerCase();
  if (!k) return '';
  return RAIL_KIND[k]?.one ?? smartCase(k);
}

/** Plural section-header form (`bank` → `Banks`). Use for any group heading
 *  that lists rails of one kind — RailPicker groups, Treasury's by-kind
 *  fallback — so the heading word matches everywhere. */
export function railKindGroupLabel(kind: string | null | undefined): string {
  const k = (kind ?? '').toLowerCase();
  if (!k) return '';
  return RAIL_KIND[k]?.many ?? smartCase(k);
}

/** Editorial sort order for kind sections. Unknown kinds sort between the
 *  known ones and `legacy`, so a new kind is visible but never on top. */
export function railKindOrder(kind: string | null | undefined): number {
  return RAIL_KIND[(kind ?? '').toLowerCase()]?.order ?? 50;
}

/**
 * Secondary line for a rail: `{Kind} · {State}`, at most two slots (§1.5).
 *
 * `kind: false` is the escape hatch for surfaces that ALREADY group by kind —
 * Treasury groups and RailPicker both print the kind as a section heading, and
 * repeating it on every row inside that section is precisely the noise the
 * operator complained about. Flat surfaces (Combobox, CommandPalette,
 * DealSlideOver, a selected-state summary) keep it, because there the kind is
 * the only thing distinguishing `Bybit` the exchange from a bank of the same
 * initial.
 *
 * `asset: true` reproduces the `Bank · AED` form from §1.5, and is OFF by
 * default: §4.4 gives the asset chip its own right-aligned `trailing` slot,
 * and printing it twice is the same clutter under another name.
 */
export function railSubtitle(row: RailLike, opts: { kind?: boolean; asset?: boolean } = {}): string {
  const slots: string[] = [];

  if (opts.kind !== false) {
    const kind = railKindLabel(row);
    if (kind) slots.push(kind);
  }

  const tier = relevanceTier(row);
  if (tier === 3) slots.push('Dormant');
  else if (tier === 2) slots.push(row.known ? 'Ready — nothing posted yet' : 'Quiet');
  else if (opts.asset && row.asset) slots.push(displayAsset(row.asset));

  return slots.join(' · ');
}

/* ══════════════════ party names ══════════════════ */

export type NameReview = 'profanity' | 'ambiguous' | 'empty' | null;

/** Title Case one token. Hyphenated compounds are opaque — `VM-OTC` and
 *  `Grow-more` are units of meaning, not two words to re-case. */
function titleToken(tok: string): string {
  if (!tok) return tok;
  if (/[-‐-―]/.test(tok)) return tok;
  const b = brandOf(tok);
  if (b) return b;
  const low = tok.toLowerCase();
  if (ACRONYMS.has(low)) return low.toUpperCase();

  // Deliberate INTERNAL capitals are data, not noise: `FuckBank`, `McKinsey`,
  // `iPhone`. Lower-casing them to `Fuckbank` would be a re-spelling, which
  // §2.1.5 forbids — normalization is casing of unstyled tokens and
  // punctuation, nothing else. Only all-lower (slug residue) and all-UPPER
  // (shouting: `KIM` → `Kim`) tokens are re-cased.
  const isAllUpper = tok === tok.toUpperCase() && tok !== low;
  const isAllLower = tok === low && tok !== tok.toUpperCase();
  if (!isAllUpper && !isAllLower) return tok;

  return low.charAt(0).toUpperCase() + low.slice(1);
}

function stripTrailingPunctuation(s: string): string {
  const words = s.split(/\s+/);
  const last = words[words.length - 1] ?? '';
  // `Liv.` keeps its dot — the punctuation is part of the brand.
  if (last && brandOf(last)) return s;
  return s.replace(/[.,;–—-]+$/, '').trimEnd();
}

function stripSourceSuffixes(s: string): string {
  let words = s.split(/\s+/).filter(Boolean);
  // Whole trailing tokens only, and never down to nothing.
  while (words.length > 1) {
    const last = words[words.length - 1] ?? '';
    const key = last.toLowerCase().replace(/[.,;]+$/, '');
    if (!SOURCE_SUFFIXES.has(key)) break;
    words = words.slice(0, -1);
  }
  return words.join(' ');
}

/**
 * A STORED display_name is operator knowledge. Render it.
 *
 * This used to strip "source suffixes" (OTC, TG, B2B, AC) and title-case every
 * token, on the theory that a stored name is raw input to be tidied. It is not
 * — a human typed it, and the tidying destroyed 41 of 143 active names on
 * go-live day:
 *
 *   "Loki OTC"          -> "Loki"        the suffix IS how the desk says it
 *   "Billa OTC"         -> "Billa"
 *   "R Telegram"        -> "R"           a party reduced to one letter; this is
 *                                        what the MC card showed as "Pay seller · R"
 *   "KIM"               -> "Kim"         an acronym, flattened
 *   "HB Office"         -> "Hb Office"
 *   "Hot wallet · ERC20"-> "Hot Wallet · Erc20"
 *
 * The file already knew: needsNameReview flags "a bare initial left behind
 * after suffix-stripping" as ambiguous. It chose to FLAG the damage rather than
 * not do it. And the profanity path already carried the right principle in
 * words — "auto-renaming destroys operator knowledge" — it just was not applied
 * to the ordinary case.
 *
 * So: if the operator capitalised ANYTHING, the name is theirs and it renders
 * verbatim. Only an all-lowercase name gets title-cased, which is a readability
 * fix and cannot lose information ("sandeep" -> "Sandeep"). Nothing is ever
 * stripped.
 *
 * Derivation from a slug is untouched — that path exists for parties with NO
 * stored name, which is what the machinery was built for.
 */
function normalizeParty(raw: string): string {
  const trimmed = collapse(raw);
  if (!trimmed) return '';
  if (/[A-Z]/.test(trimmed)) return trimmed;
  return trimmed.split(/\s+/).filter(Boolean).map(titleToken).join(' ');
}

/** Readable fallback when a party has no display name at all. */
function slugWords(slug: string): string {
  return slug.split(/[.:]/).filter(Boolean).flatMap(segmentWords).join(' ');
}

/**
 * Does this stored name need a human? Returns the reason, never a rewrite.
 * `profanity` is reported so the surface can flag it — the name still renders
 * verbatim, because auto-renaming destroys operator knowledge and masking
 * hides an identity from the one person who needs it.
 */
export function needsNameReview(raw: string): NameReview {
  const s = collapse(raw ?? '');
  if (!s) return 'empty';
  if (PROFANITY.test(s)) return 'profanity';
  const normalized = normalizeParty(s);
  if (!normalized) return 'empty';
  if (PLACEHOLDER.has(normalized.toLowerCase())) return 'ambiguous';
  // "My Ledger" — whose? The name renders as `Ledger · Desk`; only the
  // operator can say who "my" was.
  if (normalized.split(/\s+/).some((w) => POSSESSIVES.has(w.toLowerCase()))) return 'ambiguous';
  // A bare initial left behind after suffix-stripping ("R Telegram" → "R").
  const words = normalized.split(' ');
  if (words.length === 1 && normalized.length <= 2 && !ACRONYMS.has(normalized.toLowerCase()) && !brandOf(normalized)) {
    return 'ambiguous';
  }
  return null;
}

/**
 * Display name for a party. Casing and punctuation only — human names are
 * never re-spelled, and a flagged-profane name is returned untouched.
 */
export function partyName(p: PartyLike): string {
  const raw = collapse(p.display_name ?? '');
  if (raw) {
    if (needsNameReview(raw) === 'profanity') return raw;   // as-is, never masked
    return normalizeParty(raw) || raw;
  }
  const slug = (p.slug ?? '').trim();
  return slug ? slugWords(slug) || slug : '—';
}

/** The singular name of a party TYPE, for a form's toggle and an identity row.
 *
 *  'counterparty' is database plumbing. On screen these have always been
 *  MERCHANTS — Parties lists them under "Merchants", the party page's Identity
 *  row says "Merchant" — and only the new-party form still offered the raw
 *  word, so the operator picked "Counterparty" and then went looking for it
 *  among the merchants. One word, everywhere. */
export function partyTypeLabel(type: string | null | undefined): string {
  switch ((type ?? '').toLowerCase()) {
    case 'customer':     return 'Customer';
    case 'counterparty': return 'Merchant';
    case 'own_account':  return 'Own account';
    default:             return smartCase(type) || 'Party';
  }
}

/** Trust tier in words. The form that SETS the tier and the pages that show it
 *  have to spell it the same way; the form was printing the raw enum ('vip'). */
const TRUST_TIER_TEXT: Readonly<Record<string, string>> = {
  new: 'New', standard: 'Standard', repeat: 'Repeat', vip: 'VIP',
};
export function trustTierLabel(tier: string | null | undefined): string {
  return TRUST_TIER_TEXT[(tier ?? '').toLowerCase()] ?? smartCase(tier);
}

/** What kind of party this is — always shown, it is the whole point of the
 *  secondary line (a trading customer, a peer desk, and a husk look identical
 *  without it).
 *
 *  "MERCHANT" FOR EVERY COUNTERPARTY (SWEEP-SCOPE §6 "Counterparty type",
 *  item 81, 14 Sept 2026). This used to say "Merchant desk" when the role was
 *  merchant and "Counterparty" otherwise, under a Parties tab that says
 *  Merchants — one party, two words. `counterparty_role` stays in the row;
 *  the screen has one word for the type. */
export function partyKindLabel(p: PartyLike): string {
  const type = (p.party_type ?? '').toLowerCase();
  if (type === 'own_account') return p.treasury_slug ? 'Own account' : 'Group';
  if (type === 'counterparty') return 'Merchant';
  if (type === 'customer') return 'Customer';
  return 'Party';
}

/** Standing — omitted at `standard`, because the default is noise.
 *
 *  For an own account the useful "standing" is not a trust tier (it is our own
 *  money, we trust it) but WHICH RAIL backs it: `Surya AC` → `Own account ·
 *  ENBD`. That is the slot's whole job — telling two own accounts apart. */
function standingLabel(p: PartyLike): string {
  if ((p.party_type ?? '').toLowerCase() === 'own_account') {
    const provider = (p.treasury_slug ?? '').split('.')[1] ?? '';
    if (!provider) return '';
    return BRAND_MAP[provider.toLowerCase()] ?? titleToken(provider);
  }
  switch ((p.trust_tier ?? '').toLowerCase()) {
    case 'vip':    return 'VIP';
    case 'repeat': return 'Repeat';
    case 'new':    return 'New';
    default:       return '';       // 'standard', null, unknown
  }
}

/** Days-granular activity stamp: `5h ago`, `3d ago`, `12d ago`.
 *
 *  `fmtRelative` alone is not enough here — past 7 days it falls back to
 *  `fmtDate`, so a 12-day-old party would render `Aug 01, 14:32` in a slot the
 *  law specifies as `12d ago` (§2.4). Under 7 days its finer h/m resolution is
 *  genuinely better, so it is kept for that range. */
function agoLabel(iso: string): string {
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return '';
  const days = (Date.now() - t) / 86400000;
  if (days < 0) return fmtRelative(iso);
  if (days < 7) return fmtRelative(iso);
  if (days < 60) return `${Math.round(days)}d ago`;
  return `${Math.round(days / 30)}mo ago`;
}

/** State — from relevance, not from a status column.
 *
 *  `Watch only` is checked FIRST: `profile_kind = 'treasury_only'` is a
 *  durable classification that explains *why* there is no trading, whereas
 *  "Dormant" would merely restate the symptom and hide the reason. */
function stateLabel(p: PartyLike): string {
  if ((p.profile_kind ?? '').toLowerCase() === 'treasury_only') return 'Watch only';
  if (relevanceTier(p) === 3) return 'Dormant';
  const iso = activityIso(p);
  return iso ? agoLabel(iso) : '';
}

/**
 * `{Kind} · {Standing} · {State}` — muted, one line, at most 3 slots.
 * `compact` (below lg) drops Standing to 2 slots but NEVER disappears: the
 * operator asked for distinguishability at all the screens.
 */
export function partySubtitle(p: PartyLike, opts: { compact?: boolean; kind?: boolean } = {}): string {
  // `kind: false` drops the kind word — on a list already filtered to one
  // kind (the phone's Customers tab) "Customer" on every row says nothing.
  const slots: string[] = opts.kind === false ? [] : [partyKindLabel(p)];
  if (!opts.compact) {
    const standing = standingLabel(p);
    if (standing) slots.push(standing);
  }
  const state = stateLabel(p);
  if (state) slots.push(state);
  return slots.join(' · ');
}

/* ══════════════════ relevance ══════════════════ */

/** Per-asset dust. A flat `|balance| >= 1` would hide half a bitcoin. */
// MUST AGREE WITH bff/src/routes/settlements.ts DUST. A stablecoin was missing
// here and fell to the default of one whole unit, so a merchant the settle
// screen listed as owing 0.70 USDT read "square" on Home and on their own page
// — one party, two answers, and the smaller one is the one that stops the
// operator chasing (3 Sept audit).
export const DUST: Readonly<Record<string, number>> = {
  AED: 1, INR: 1, USD: 1, EUR: 1, GBP: 1,
  USDT: 0.5, USDC: 0.5,
  BTC: 0.0001, ETH: 0.001, SOL: 0.01, XLM: 0.1,
};
export const DUST_DEFAULT = 1;

/** `USDTTRC20` → `USDT` so chain variants share one threshold. */
function baseAsset(code: string | null | undefined): string {
  if (!code) return '';
  const up = code.toUpperCase();
  if (up.startsWith('USDT')) return 'USDT';
  if (up.startsWith('USDC')) return 'USDC';
  return up;
}

/** A party's open boxes in DESK WORDS for picker rows (operator, 2 Sept:
 *  "make customer selection easy and identifiable"): at a money desk a name
 *  identifies nobody — who they are AND what stands between you does.
 *  Positive = they owe the desk; negative = the desk holds theirs (the sign
 *  convention proven on Loki's 70,000). Top three boxes by size, folded codes,
 *  Indian grouping; an empty bag reads "square". */
export function partyBoxWords(p: RelevanceLike): string {
  // PER-ASSET DUST, AND THE BOOKS' OWN NUMBER FORMAT. This dropped every box
  // under one unit whatever the asset, and formatted with a bare
  // toLocaleString: a merchant the settle screen showed as "owes you 0.70
  // USDT" read "square" on Home, and a box /settle prints as 36,711 AED read
  // 36,710.5 here. One party, two screens, three answers. `dustFor` knows a
  // coin from a dirham, and `fmtAmount` is the formatter /settle already uses.
  const entries = balanceEntries(p)
    .filter(([a, v]) => Math.abs(v) > dustFor(a))
    .sort((a, b) => Math.abs(b[1]) - Math.abs(a[1]))
    .slice(0, 3);
  if (entries.length === 0) return 'square';
  return entries
    .map(([a, v]) => `${v > 0 ? 'owes you' : 'you hold'} ${fmtAmount(Math.abs(v), foldAsset(a))} ${foldAsset(a)}`)
    .join(' · ');
}

export function dustFor(asset: string | null | undefined): number {
  return DUST[baseAsset(asset)] ?? DUST_DEFAULT;
}

/** Every (asset, balance) pair a row carries, in either payload shape. */
function balanceEntries(x: RelevanceLike): Array<[string, number]> {
  const out: Array<[string, number]> = [];
  if (typeof x.balance === 'number' && Number.isFinite(x.balance)) {
    out.push([x.asset ?? '', x.balance]);
  }
  const bag = x.balances;
  if (Array.isArray(bag)) {
    for (const b of bag) {
      const v = b?.balance;
      if (typeof v === 'number' && Number.isFinite(v)) out.push([b?.asset ?? '', v]);
    }
  } else if (bag && typeof bag === 'object') {
    for (const [asset, v] of Object.entries(bag)) {
      if (typeof v === 'number' && Number.isFinite(v)) out.push([asset, v]);
    }
  }
  return out;
}

/** Largest |balance| the row carries. Cross-asset compare is a deliberate
 *  heuristic (already shipping in RailPicker) applied only WITHIN a tier or
 *  group — no display-layer USD conversion is permitted anywhere. */
export function magnitude(x: RelevanceLike): number {
  let max = 0;
  for (const [, v] of balanceEntries(x)) max = Math.max(max, Math.abs(v));
  return max;
}

function activityIso(x: RelevanceLike): string {
  const iso = x.last_activity_at ?? x.lastActivityAt ?? '';
  return typeof iso === 'string' ? iso : '';
}

/** Days since the last known activity, or null when the endpoint doesn't say. */
function daysSinceActivity(x: RelevanceLike): number | null {
  const iso = activityIso(x);
  if (!iso) return null;
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return null;
  return (Date.now() - t) / 86400000;
}

/**
 * 0 PINNED · 1 LIVE · 2 QUIET · 3 STALE.
 * "Stale" means exactly tier 3 — no other definition exists in this codebase.
 * A favourite is never stale: pinning is an operator override.
 */
export function relevanceTier(x: RelevanceLike): Tier {
  if (x.is_favorite) return 0;
  // A RAIL THE DESK HAS SAID IT USES is not a husk because nothing has landed
  // on it yet. At zero with no history it fell to STALE and the picker parked
  // it behind "More" — the operator, with the office safe's EUR registered
  // that morning, reported "I still don't see eur here" (12 Sept 2026). Quiet,
  // in its group, one tap away.
  if (x.known) return 2;

  const entries = balanceEntries(x);

  // §3.5 DEGRADATION — the single most important line in this function.
  //
  // A row that carries NO balance data and NO activity data is not a husk; it
  // is an endpoint that does not say. `GET /api/parties` selects neither (see
  // bff/src/routes/parties.ts list SELECT), so without this guard every one of
  // the ~90 parties falls through to STALE: the subtitle reads "Dormant" on
  // all of them and `partitionByRelevance` hides the entire list behind
  // `Show all (90)`. Absence of evidence is not evidence of a husk.
  //
  // Degraded behaviour is exactly: favourite → 0, everything else → 1. Nothing
  // renders "Dormant" without data to support it, and nothing is hidden on a
  // list that cannot compute staleness.
  if (entries.length === 0 && daysSinceActivity(x) === null) return 1;

  let live = false;
  let nonZero = false;
  for (const [asset, v] of entries) {
    const abs = Math.abs(v);
    if (abs >= dustFor(asset)) live = true;
    if (abs > 0) nonZero = true;
  }
  if (live) return 1;

  const days = daysSinceActivity(x);
  if (days !== null && days <= 30) return 1;
  if (nonZero) return 2;                       // sub-dust but not empty
  if (days !== null && days <= 90) return 2;
  return 3;
}

export function isStale(x: RelevanceLike): boolean {
  return relevanceTier(x) === 3;
}

/** Numeric sort key, lower first: tier dominates, |balance| breaks ties.
 *  Stays inside `[tier - 1, tier)` so tiers can never interleave. */
export function relevanceRank(x: RelevanceLike): number {
  return relevanceTier(x) - 1 + 1 / (1 + magnitude(x));
}

function sortName(x: NameLike): string {
  if (x.display_name || x.party_type) return partyName(x);
  return railName(x);
}

/** The one comparator: tier asc → |balance| desc → recency desc → name asc.
 *  Screens never re-implement this; they pass `maxTier`, not predicates. */
export function compareRelevance(a: NameLike, b: NameLike): number {
  const ta = relevanceTier(a);
  const tb = relevanceTier(b);
  if (ta !== tb) return ta - tb;

  const ma = magnitude(a);
  const mb = magnitude(b);
  if (ma !== mb) return mb - ma;

  const ra = new Date(activityIso(a)).getTime();
  const rb = new Date(activityIso(b)).getTime();
  const va = Number.isFinite(ra) ? ra : -Infinity;
  const vb = Number.isFinite(rb) ? rb : -Infinity;
  if (va !== vb) return vb - va;

  return sortName(a).localeCompare(sortName(b));
}

/**
 * Split rows into what a surface shows and what it holds back, both sorted by
 * `compareRelevance`. `hidden.length` IS the N in `Show all (N)` / `+ N quiet
 * accounts` — any surface that hides rows without showing a true count is a
 * bug.
 */
export function partitionByRelevance<T extends NameLike>(
  xs: readonly T[],
  opts: { maxTier?: Tier } = {},
): { shown: T[]; hidden: T[] } {
  const maxTier = opts.maxTier ?? 2;
  const sorted = [...xs].sort(compareRelevance);
  const shown: T[] = [];
  const hidden: T[] = [];
  for (const x of sorted) {
    if (relevanceTier(x) <= maxTier) shown.push(x);
    else hidden.push(x);
  }
  return { shown, hidden };
}

/* ═══════════════════════════════ SELF-CHECK ═══════════════════════════════
   Every line below is ACTUAL OUTPUT, captured by running these exact strings
   from the live book (entity 'dubaiotc', 2026-08-12) through the compiled
   module — not intent, not a wish. Re-run after touching BRAND_MAP, the
   grammar, or the tier thresholds; 29/29 rails and 26/26 parties must hold.

   RAILS — railName({ label })                              rule exercised
     'Wio — Shweta'          → 'Wio · Shweta'               separator normalized
     'ENBD — NK'             → 'ENBD · NK'                  initials stay caps
     'Mashreq — NK'          → 'Mashreq · NK'
     'RAK — Bhagyashwari'    → 'RAK · Bhagyashwari'         human name untouched
     'Live — Bhavy'          → 'Live · Bhavy'               unmapped brand, 2 slots
     'Office cash'           → 'Office cash'                noun kept (vs Office entity)
     'Angadiya cash · India' → 'Angadiya cash · India'      byte-identical no-op
     'Bybit'                 → 'Bybit'
     'Htx'                   → 'HTX'                        brand map beats smartCase
     'Okx'                   → 'OKX'
     'Kucoin'                → 'KuCoin'
     'Bitget'                → 'Bitget'
     'Esub Bybit'            → 'Bybit · Esub'               brand-first reversal
     'Esub Binance'          → 'Binance · Esub'
     'Esub Gate'             → 'Gate · Esub'
     'Acquired accounts'     → 'Acquired accounts'          bucket noun, one slot
     'Htx India'             → 'HTX · India'                region → qualifier
     'Okx India'             → 'OKX · India'
     'Kucoin India'          → 'KuCoin · India'
     'Paxful India'          → 'Paxful · India'
     'Binance India'         → 'Binance · India'
     'IndiaOTC — RBL'        → 'RBL · India'                entity prefix dropped
     'IndiaOTC — PNB'        → 'PNB · India'
     'IndiaOTC — AU'         → 'AU · India'
     'Bhargav — SBI'         → 'SBI · Bhargav'              holder beats region
     'Sai — SIB'             → 'SIB · Sai'
     'Working wallet'        → 'Working wallet'
     'My Ledger'             → 'Ledger · Desk'              + review 'ambiguous'
     "Loki's Ledger"         → 'Ledger · Loki'              possessive dissolved
   Longest is 'RAK · Bhagyashwari' (21) — all 29 inside the 28-char budget.

   RAILS FROM STRUCTURE (no operator label)
     slug 'cash.dxb'              → 'DXB cash'
     slug 'exchange.binance.esub' → 'Binance · Esub'
     slug 'hot.trc.v7'            → 'V7'          chain dropped, asset chip has it
     slug 'cold.ledger.loki'      → 'Ledger · Loki'
     slug 'bank.enbd.nk'          → 'ENBD · Nk'   KNOWN degradation: a slug has
       lost the original capitals, so the 2–3-char initials rule cannot fire.
       `row.label` is authoritative and the BFF now returns it, so this path is
       the fallback, not the norm. Do not "fix" it by upper-casing all short
       holders — that would turn 'Sai' into 'SAI'.
     account 'treasury:dubaiotc:cash:office:fiat:aed' → 'Office cash'

   PARTIES — partyName({ display_name })
     'FuckBank Customer' → 'FuckBank Customer'  ⚑profanity — verbatim, never renamed
     'GRYPTO TG'         → 'Grypto'
     'Chris TG'          → 'Chris'
     'R Telegram'        → 'R'                  ⚑ambiguous
     'Mandosa B2B'       → 'Mandosa'
     'Loki OTC'          → 'Loki'
     'HK'                → 'HK'                 acronym
     'JP Office'         → 'JP Office'
     'Kevin'             → 'Kevin'
     'Thanos'            → 'Thanos'
     'Billa OTC'         → 'Billa'
     'VM-OTC'            → 'VM-OTC'             hyphenated compound survives
     'Surya AC'          → 'Surya'
     'Sriniwas Pune'     → 'Sriniwas Pune'
     'Deepak Patel'      → 'Deepak Patel'
     'Abdellah'          → 'Abdellah'
     'Irogbe'            → 'Irogbe'
     'KIM'               → 'Kim'                ALLCAPS is shouting, not meaning
     'Lamzira AMC'       → 'Lamzira AMC'
     'Blockgemini'       → 'Blockgemini'
     'Victoria'          → 'Victoria'
     'Oussama Ref.'      → 'Oussama Ref'        dot only (see SOURCE_SUFFIXES)
     'Grow-more'         → 'Grow-more'          deliberate casing left alone
     'Kush'              → 'Kush'
     'Sanjeev Kumar'     → 'Sanjeev Kumar'
     'Sandeep'           → 'Sandeep'
   No false profanity flags: 'Assam Traders' → null, 'Dick Patel' → null.

   SUBTITLES — partySubtitle(p, opts)
     customer + repeat + 3d        → 'Customer · Repeat · 3d ago'
       …{ compact: true }          → 'Customer · 3d ago'          (Standing drops first)
     counterparty + merchant + 12d → 'Merchant · 12d ago'
     customer + new + 400d + 0 bal → 'Customer · New · Dormant'
     own_account + bank.enbd.surya → 'Own account · ENBD'
     profile_kind 'treasury_only'  → 'Customer · Watch only'
     own_account + no treasury_slug→ 'Group'                      (BFF is_group convention)
     no balance + no activity      → 'Customer'   §3.5 — never invents 'Dormant'

   railSubtitle(row, opts)
     bank, live                    → 'Bank'
     bank, live, { asset: true }   → 'Bank · AED'
     cold, 0 BTC, cold             → 'Cold wallet · Dormant'
     { kind: false } inside a group→ 'Dormant'

   RELEVANCE — relevanceTier()
     { balance: 0.5,   asset: 'BTC' }              → 1   dust 0.0001, NOT hidden
     { balance: 0.5,   asset: 'AED' }              → 2   sub-dust but real
     { balance: 0,     asset: 'AED' }              → 3
     { balance: 0.005, asset: 'ETH' }              → 1
     { balance: 900,   asset: 'USDTTRC' }          → 1
     { balance: 0, is_favorite: true }             → 0   pin overrides staleness
     { }  ← the parties-list row shape             → 1   §3.5 degradation
   90 party rows with no balance/activity → partitionByRelevance hides 0.
   Mixed rail list: shown/hidden partition loses no row, and maxTier 3 hides
   nothing — Treasury can foot with the same helper the pickers use.

   INVARIANTS spot-checked: railName and partyName are idempotent (safe to run
   the rename script and then re-render its own output), total on {} / null /
   '' / '   ' / '·' / punctuation-only labels, and never return '' or undefined.
   ══════════════════════════════════════════════════════════════════════════ */

/* ══════════════════ picker ranking ══════════════════ */

/**
 * Order party rows by how well they answer what was TYPED, then cut.
 *
 * Every picker in this console did `.slice(0, 10)` straight off the endpoint,
 * and the endpoint orders by slug. On 2026-08-24 the operator typed "r" into
 * the MC customer field looking for R Telegram: the server returned 14 rows,
 * alphabetically, and `r-telegram` sat at index 10 — one past the cut. The
 * picker showed ten other customers and none of the one being searched for,
 * which reads as "this party does not exist" and invites onboarding a duplicate.
 *
 * A cap is still wanted — a dropdown of 90 names helps nobody. It just has to
 * cut the WORST matches rather than the alphabetically-latest ones:
 *   0  slug or label starts with the query
 *   1  a word inside the name starts with it
 *   2  it appears anywhere
 *   3  no textual hit (the server matched on something else, e.g. a handle)
 * Ties keep the endpoint's own order, so the list stays stable as it narrows.
 */
export function rankForQuery<T extends { slug?: string; display_name?: string | null }>(
  rows: readonly T[], query: string, limit = 25,
): T[] {
  const fold = (s: string) => s.toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
  const q = fold(query);
  if (!q) return rows.slice(0, limit);

  const score = (r: T): number => {
    const hay = `${fold(r.slug ?? '')} ${fold(r.display_name ?? '')}`.trim();
    if (fold(r.slug ?? '').startsWith(q) || fold(r.display_name ?? '').startsWith(q)) return 0;
    if (hay.split(' ').some((w) => w.startsWith(q))) return 1;
    if (hay.includes(q)) return 2;
    return 3;
  };
  return rows
    .map((r, i) => ({ r, s: score(r), i }))
    .sort((a, b) => (a.s - b.s) || (a.i - b.i))
    .slice(0, limit)
    .map((x) => x.r);
}


/* ══════════════════ money-movement vocabulary ══════════════════
 *
 * Two enums the ledger speaks and the operator does not. They live here beside
 * `partyTypeLabel` / `railKindName` / `trustTierLabel` for the same reason
 * those do: a raw enum must never reach the DOM (conformance rule 1), and one
 * table is the only way two screens can agree on a word.
 *
 * The SENTENCE on a row is not built here — the API builds it once, server-side
 * (bff/src/lib/movements.ts), so "received" on the ledger cannot become
 * "credit" on a statement. These are only the short labels: a filter chip, a
 * row pill, a column heading.
 */

/** The buckets GET /api/movements sorts the desk's money into. Deliberately
 *  not the ledger's own words: `expense` is a COST on this desk, and `owner`
 *  covers capital going in and drawings coming out. `p2p` (11 Sept 2026) is a
 *  balance moved from one party to another — the desk's own money never moved,
 *  so it is not "Money in" or "Money out" and must not read as either. */
const MOVEMENT_KIND_TEXT: Readonly<Record<string, string>> = {
  money_in:   'Money in',
  money_out:  'Money out',
  rail_move:  'Move',
  p2p:        'Between parties',
  adjustment: 'Adjustment',
  expense:    'Cost',
  owner:      'Owner',
};
export function movementKindLabel(kind: string | null | undefined): string {
  return MOVEMENT_KIND_TEXT[(kind ?? '').toLowerCase()] ?? (smartCase(kind) || 'Movement');
}

/** A ledger transaction's `metadata.type`, in the desk's words.
 *
 *  A party's statement carries the RAW type on every row — 'transfer',
 *  'party_payment_split', 'mc_deliver_balance', 'equity_drawing' — because that
 *  is what the books recorded. It groups here into the handful of things the
 *  operator actually did, so the column reads as a kind and not as a database
 *  dump. The trailing `smartCase` is the safety net: a type nobody has mapped
 *  yet still renders as words, never as a slug. */
const LEDGER_TYPE_TEXT: Readonly<Record<string, string>> = {
  // The brought-forward row the statement synthesises — no transaction made it.
  opening:              'Brought forward',
  opening_balance:      'Opening',
  // Record money for a party. `transfer` is the ordinary shape; the split is
  // the same movement with part of it booked as a cost to the desk.
  transfer:             'Money',
  party_payment_split:  'Money',
  receipt:              'Money in',
  payment:              'Money out',
  // Trades. They belong on a party's statement — "everything that touched
  // them" — even though the movement ledger excludes them.
  trade:                'Trade',
  trade_unsettled:      'Trade',
  trade_margin:         'Margin',
  // Corrections. adjustments.ts writes `rebalance`, whatever the plan said.
  rebalance:            'Adjustment',
  adjustment:           'Adjustment',
  correction:           'Correction',
  reclass:              'Reclass',
  // Manager's cheque.
  mc_book:              'Cheque',
  mc_pay_seller:        'Cheque',
  mc_pay_seller_fx:     'Cheque',
  mc_deliver_cheque:    'Cheque',
  mc_deliver_balance:   'Cheque',
  mc_deliver_fx:        'Cheque',
  mc_fee:               'Cheque fee',
  mc_seller_fee:        'Cheque fee',
  mc_fee_discharge:     'Cheque fee',
  // Owner money. The writers post equity_capital / equity_drawing (and two
  // legacy bare `drawing` rows) — never the `equity_in` the plan named.
  equity_capital:       'Capital',
  equity_drawing:       'Drawing',
  drawing:              'Drawing',
  expense:              'Cost',
  // The desk's share of a sub-broker's balance (17 Sept 2026) — the Money
  // screen files it under Adjustment; the statement names it.
  profit_share:         'Profit share',
  // A transaction that carried no type at all.
  movement:             'Movement',
};
/** A REFERENCE, SHORT ENOUGH FOR A COLUMN (operator, 18 Sept 2026: "fix the
 *  reference column too" — the full id clipped and scrolled the Money table
 *  sideways). The desk's references are `KIND-YYYY-MM-DD-CODE`; the day is
 *  already the row's When, so the column shows the kind and the code —
 *  "DEAL 8W5EJ", "PAY HNL4L", "RCPT MSTED" — and the full id rides on the
 *  tooltip and the copy. The two machine ids the ledger still carries read as
 *  what they are. Anything else is shown whole. */
export function shortReference(ref: string): string {
  const m = /^([A-Z0-9]+)-\d{4}-\d{2}-\d{2}-(.+)$/.exec(ref);
  if (m) return `${m[1]} ${m[2]}`;
  if (ref.startsWith('open:')) return 'Opening';
  if (ref.startsWith('legacy2:')) return 'Carried in';
  return ref;
}

export function ledgerTypeLabel(type: string | null | undefined): string {
  const t = (type ?? '').toLowerCase();
  if (!t) return 'Movement';
  return LEDGER_TYPE_TEXT[t] ?? (smartCase(t.replace(/[:_]+/g, ' ')) || 'Movement');
}

/* ══════════════════ valuation vocabulary ══════════════════
 *
 * A valued figure is never allowed on screen bare (decision of 4 Sept 2026): it
 * says what it is, where the price came from and when that price was true. Two
 * of those three are words, and words live here — the same law as BRAND_MAP and
 * LEDGER_TYPE_TEXT. A source or a basis nobody has mapped yet still renders as
 * words through `smartCase`, never as the raw slug the database holds.
 */

/** `admin.asset_valuations.source` in the desk's words. The seeded source is
 *  the operator's own hardware wallet, read on 4 Sept 2026 — the same screen
 *  the quantities were read off, which is the whole reason the price is
 *  trustworthy enough to state. */
const VALUATION_SOURCE_TEXT: Readonly<Record<string, string>> = {
  'ledger-device-4sept': 'the Ledger device screen',
};
export function valuationSourceName(source: string | null | undefined): string {
  const s = (source ?? '').trim().toLowerCase();
  if (!s) return 'an unnamed source';
  return VALUATION_SOURCE_TEXT[s] ?? (smartCase(s) || 'an unnamed source');
}

/** How a figure was arrived at — `Basis` in bff/src/lib/valuations.ts. The
 *  precedence there is evidence-first, and these words carry that distinction
 *  out to the screen: "own trades" is something the desk DID, "stated price"
 *  is something somebody SAID. The caption is the operator's — "own trades",
 *  not "desk's own trades": on the desk's own screen the desk is implied
 *  (Phase C review, 14 Sept 2026). */
const VALUATION_BASIS_TEXT: Readonly<Record<string, string>> = {
  identity:    'same currency',
  peg:         'AED peg',
  cross:       'INR cross',
  dealt:       'own trades',
  dealt_cross: 'own trades, via INR',
  family:      'stablecoin, 1:1',
  stated:      'stated price',
};
export function valuationBasisName(basis: string | null | undefined): string {
  const b = (basis ?? '').trim().toLowerCase();
  if (!b) return 'no price';
  return VALUATION_BASIS_TEXT[b] ?? (smartCase(b) || 'no price');
}

/* ══════════════════ the running account ══════════════════
 *
 * Operator, 4 Sept 2026, looking at DEAL-2026-09-03-GL7BH: "15705 is loki
 * profit and it should stay in the loki balance, no need to pay and not
 * actually paid", and then — so it could not be read as a one-off — "each one
 * of loki's trade will follow similar pattern, his profit gets accumulated in
 * his account".
 *
 * A party the operator has MARKED (admin.parties.trade_remainder_rides_on_account,
 * db/0036) runs an account with the desk on which the remainder of a trade leg
 * rides. Four surfaces have to say the same thing about it — the settle screen,
 * the settle button's refusal, the deal panel and the party page — so the words
 * live here once, beside every other piece of desk vocabulary. Repeat them and
 * they drift, and a rule the screens explain three ways is a rule nobody can
 * hold.
 *
 * NOTHING HERE IS A THRESHOLD, and no screen may invent one. Riding is a
 * property of the PARTY: a percentage or a "close enough" floor would close a
 * genuinely underpaid trade the moment somebody had paid enough of it, which is
 * the 2 Sept failure the settle guard exists for. The only place the
 * arrangement can be turned on is the party's own page.
 */

/** One asset's worth of remainder that stayed on a party's balance instead of
 *  being paid. Shipped on a settled leg (`LegState.rode`), on the sweep's
 *  `swept[]` rows and, summed, on the party page's `carried_on_account`. */
export type Rode = { asset: string; amount: number };

export const RUNNING_ACCOUNT_TITLE = 'Running account';

/** THE RULE — both halves of it, always together. The second sentence is not
 *  decoration: an arrangement described only as "remainders ride here" reads
 *  like a party whose trades never have to be paid, and the guard that stops
 *  an unpaid trade closing is the whole reason this is safe to switch on. */
export const RUNNING_ACCOUNT_RULE =
  'A trade leg closes as soon as money has moved on it, and whatever is left over rides on this balance — '
  + 'usually their own margin, which the two of you settle separately. A leg nothing has moved on is an unpaid '
  + 'trade and still holds the trade open, exactly as it does for anybody else.';

/** What it means for a party WITHOUT the arrangement, said plainly so the
 *  switch reads as a choice between two stated behaviours rather than a
 *  feature toggle. */
export const RUNNING_ACCOUNT_OFF_RULE =
  'Every leg has to be paid in full: a part-paid leg keeps the trade on the settle screen until the rest moves, '
  + 'or until you close it by hand.';

/** Where the money actually is, for a marked party. It is NOT a second pot —
 *  it is the balance already on the page — and saying so is the point.
 *  A CLAUSE, not a sentence: it is printed after "Running account — ". */
export const RUNNING_ACCOUNT_BALANCE_NOTE =
  'what is carried on account is the balance above; there is no second pot. It is real money between you, '
  + 'and it stays here until you settle it.';

/** "owed to the angadiya" — the words under a RAIL's negative balance on
 *  Treasury when that rail is a running account (api `running_account`, from
 *  bff lib/buckets.ts RUNNING_ACCOUNT_RAILS). A negative running account is
 *  the chart's design — the desk draws on it and settles later (operator,
 *  3 Sept) — so the figure wears the neutral colour and this label, not the
 *  destructive red that means "a withdrawal was booked twice". The Sheet files
 *  the same money as "Owed to running accounts"; this is that line, said for
 *  the one rail in front of the operator.
 *
 *  The noun is the rail's kind word — the last slug segment, `cash.angadiya`
 *  → angadiya — which is how the desk refers to it ("through the angadiya",
 *  RecordMoney.tsx), not its stored label, which is a proper name and would
 *  read "owed to the Angadiya cash · India". */
export function owedToRunningAccountWords(rail: RailLike): string {
  const noun = (rail.slug ?? '').split('.').pop()?.replace(/-/g, ' ').toLowerCase() ?? '';
  return noun ? `owed to the ${noun}` : 'owed to this running account';
}

/** "15,705 INR" · "15,705 INR and 40 USDT" — amounts only, for a sentence that
 *  supplies its own verb. Tolerates a missing or empty list so a screen reading
 *  an older payload prints nothing rather than crashing. */
export function rodeAmountWords(rode: readonly Rode[] | null | undefined): string {
  return joinList(liveRode(rode).map(
    (r) => `${fmtAmount(r.amount, foldAsset(r.asset))} ${displayAsset(foldAsset(r.asset))}`));
}

/** "15,705 INR from 2 trades" · "15,705 INR from 2 trades and 40 USDT from 1
 *  trade" — the party page's running total of what has ridden in.
 *
 *  THE COUNT IS PER ASSET because that is how it arrives, and adding the counts
 *  together would be wrong: one trade can ride on both of its legs, and the
 *  page would then report it twice. */
export function carriedRodeWords(rode: readonly (Rode & { trades?: number })[] | null | undefined): string {
  return joinList(liveRode(rode).map((r) => {
    const n = Number(r.trades);
    const from = Number.isFinite(n) && n > 0 ? ` from ${n} trade${n === 1 ? '' : 's'}` : '';
    return `${fmtAmount(r.amount, foldAsset(r.asset))} ${displayAsset(foldAsset(r.asset))}${from}`;
  }));
}

/** Rows worth printing: a zero or a malformed amount is not a remainder, and a
 *  missing list is an older payload, not an empty arrangement. */
function liveRode<T extends Rode>(rode: readonly T[] | null | undefined): Array<T & { amount: number }> {
  return (rode ?? [])
    .map((r) => ({ ...r, amount: Number(r?.amount) }))
    .filter((r) => Number.isFinite(r.amount) && r.amount > 0);
}

/** `a` · `a and b` · `a, b and c`. */
function joinList(parts: string[]): string {
  if (parts.length === 0) return '';
  if (parts.length === 1) return parts[0]!;
  return `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]!}`;
}

/** "15,705 INR rode on the running account" — the SAME sentence the BFF writes
 *  into the deal's note and its audit row (bff/src/routes/settlements.ts
 *  rodeWords). The operator reads the note on the deal and this line on the
 *  screen; they have to be the same words or they read as two different events.
 *  Empty when nothing rode, so callers can test it directly. */
export function rodeSentence(rode: readonly Rode[] | null | undefined): string {
  const words = rodeAmountWords(rode);
  return words ? `${words} rode on the running account` : '';
}
